import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { accessLocalStorage, getAllInternalConfigs, deleteInternalConfig } from '../../../services';
import { hasFeature } from '../../../services/permissions';

import PbxContext from '../../../context/PbxContext';
import '../../../libs/bulma.min.css';
import './InternalCallsList.css';

import Loading from '../../../components/Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faPlus, faEye, faPhone, faChartLine } from '@fortawesome/free-solid-svg-icons';

function InternalCallsList() {
  const getItensStateGlobal = useContext(PbxContext);
  const { toggleIsChangeFormElements, toggleIsHidden, setItemsSelectedToEdit } = getItensStateGlobal;

  let url = window.location.href;
  url = url.split('/')[2];

  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [configsSelected, setConfigsSelected] = useState([]);
  const [disable, setDisable] = useState(true);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const history = useHistory();

  const fetchConfigs = async () => {
    try {
      // Obter dados do usuário para determinar o tenant
      const dataUser = await accessLocalStorage.getUserLocalStorage();
      let filters = {};
      
      // Apenas usuários com role = "root" podem especificar tenant via query parameter
      if (dataUser && dataUser.user && dataUser.user.role === 'root' && dataUser.user.idtenant) {
        filters.tenant = dataUser.user.idtenant;
      }
      // Para usuários não-root, não enviar filtro de tenant (usar o do token automaticamente)
      
      const response = await getAllInternalConfigs(filters);
      if (response && response.success && response.data) {
        setConfigs(response.data);
      } else {
        setConfigs([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Erro ao buscar configurações:', error);
      setConfigs([]);
      setLoading(false);
    }
  };

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');

    const { user: { role } } = dataUser;
    setUserRole(role);

    await fetchConfigs();
  }, [history]);

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  const handleEdit = (config) => {
    setItemsSelectedToEdit(config);
    toggleIsChangeFormElements();
    toggleIsHidden();
    history.push('/config/chamadas/internas/editar');
  };

  const handleView = (config) => {
    setItemsSelectedToEdit(config);
    history.push('/config/chamadas/internas/visualizar');
  };

  const confirmDelete = (config) => {
    setConfigToDelete(config);
    setShowConfirmDelete(true);
  };

  const handleDelete = async () => {
    if (!configToDelete) return;

    try {
      const response = await deleteInternalConfig(configToDelete.id);
      if (response && response.success) {
        await fetchConfigs();
        setShowConfirmDelete(false);
        setConfigToDelete(null);
      } else {
        console.error('Erro ao deletar configuração:', response);
      }
    } catch (error) {
      console.error('Erro ao deletar configuração:', error);
    }
  };

  const cancelDelete = () => {
    setShowConfirmDelete(false);
    setConfigToDelete(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'is-success' : 'is-danger';
  };

  const getStatusText = (isActive) => {
    return isActive ? 'Ativo' : 'Inativo';
  };

  if (loading) return <Loading />;

  return (
    <div className="internal-calls-container">
      <div className="internal-calls-header">
        <div className="internal-calls-title-wrapper">
          <svg className="internal-calls-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <div>
            <h1 className="internal-calls-title">Chamadas Internas</h1>
            <p className="internal-calls-subtitle">Gerenciar configurações de roteamento interno</p>
          </div>
        </div>
        <div className="internal-calls-actions">
          {hasFeature('config.chamadas.internas', 'create') && (
          <Link to="/config/chamadas/internas/nova" className="btn-new-internal-call">
            <FontAwesomeIcon icon={faPlus} />
            Nova Configuração
          </Link>
          )}
        </div>
      </div>

      {configs.length === 0 ? (
        <div className="empty-state">
          <svg className="empty-state-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <div className="empty-state-title">Nenhuma configuração encontrada</div>
          <p className="empty-state-text">
            Clique em "Nova Configuração" para criar sua primeira configuração de chamada interna.
          </p>
        </div>
      ) : (
        <div className="internal-calls-table-wrapper">
          <table className="internal-calls-table">
            <thead className="internal-calls-table-header">
              <tr>
                <th>ID</th>
                <th>Tenant</th>
                <th>Máscara</th>
                <th>Destino Principal</th>
                <th>Tipo</th>
                <th>Transbordos</th>
                <th>Status</th>
                <th>Criado em</th>
                <th>Atualizado em</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((config) => (
                <tr key={config.id}>
                  <td>
                    <strong>#{config.id}</strong>
                  </td>
                  <td>
                    <span className="internal-badge badge-tenant">
                      {config.tenant}
                    </span>
                  </td>
                  <td>
                    <span className="internal-badge badge-mask">
                      {config.mask}
                    </span>
                  </td>
                  <td>
                    <span className="internal-badge badge-destination">
                      {config.destination_main}
                    </span>
                  </td>
                  <td>
                    <span className={`internal-badge ${
                      config.destination_type_main === 'endpoint' ? 'badge-endpoint' :
                      config.destination_type_main === 'queue' ? 'badge-queue' :
                      config.destination_type_main === 'disa' ? 'badge-disa' : 'badge-light'
                    }`}>
                      {config.destination_type_main === 'endpoint' ? 'Endpoint' :
                       config.destination_type_main === 'queue' ? 'Queue' :
                       config.destination_type_main === 'disa' ? 'DISA' : 
                       config.destination_type_main || 'endpoint'}
                    </span>
                  </td>
                  <td>
                    <span className="internal-badge badge-overflow-count">
                      {config.overflows ? config.overflows.length : 0} transbordo(s)
                    </span>
                  </td>
                  <td>
                    <span className={`internal-badge ${config.active ? 'badge-active' : 'badge-inactive'}`}>
                      {getStatusText(config.active)}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {formatDate(config.created_at)}
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {formatDate(config.updated_at)}
                  </td>
                  <td>
                    <div className="internal-calls-actions-cell">
                      <button
                        className="btn-internal-call-action btn-view-internal-call"
                        onClick={() => handleView(config)}
                        title="Visualizar"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        className="btn-internal-call-action btn-edit-internal-call"
                        onClick={() => handleEdit(config)}
                        title="Editar"
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      {hasFeature('config.chamadas.internas', 'delete') && (
                        <button
                          className="btn-internal-call-action btn-delete-internal-call"
                          onClick={() => confirmDelete(config)}
                          title="Excluir"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="internal-calls-stats">
        <p className="internal-calls-stats-text">
          Total: {configs.length} configuração(ões)
        </p>
      </div>

      {showConfirmDelete && (
        <div className="delete-modal-overlay">
          <div className="delete-modal-card">
            <header className="delete-modal-header">
              <p className="delete-modal-title">Confirmar Exclusão</p>
              <button 
                className="modal-close-btn" 
                aria-label="close"
                onClick={cancelDelete}
              >
                ×
              </button>
            </header>
            <section className="delete-modal-body">
              <p>
                Tem certeza que deseja excluir a configuração{' '}
                <strong>#{configToDelete?.id}</strong> com máscara{' '}
                <strong>{configToDelete?.mask}</strong>?
              </p>
              <div className="delete-modal-warning">
                <strong>Esta ação não pode ser desfeita!</strong>
              </div>
            </section>
            <footer className="delete-modal-footer">
              <button 
                className="btn-modal btn-modal-cancel"
                onClick={cancelDelete}
              >
                Cancelar
              </button>
              <button 
                className="btn-modal btn-modal-delete"
                onClick={handleDelete}
              >
                Sim, Excluir
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

export default InternalCallsList;
